export type SearchFilters = {
    q?: string;
    visa: string;
    visaDetail: string;
    industry: string;
    jobDetail: string;
    location: string[];
    gender?: 'nam' | 'nu' | 'nam-nu' | 'cap-doi' | 'vo-chong' | '';
    height?: [number, number];
    weight?: [number, number];
    age?: [number, number];
    visionRequirement?: string;
    tattooRequirement?: string;
    smokingStatus?: string;
    drinkingStatus?: string;
    dominantHand?: string;
    bangladeshAgency?: string;
    maritalStatus?: string;
    childrenStatus?: string;
    hasWorkedAbroad?: string;
    workedCountries?: string[];
    currentSalary?: string;
    currentSalaryMin?: number;
    currentSalaryMax?: number;
    desiredSalaryVietnam?: string;
    desiredSalaryVietnamMin?: number;
    desiredSalaryVietnamMax?: number;
    educationRequirement?: string;
    englishScore?: string;
    englishLevel?: string;
    vietnameseCertificate?: string;
    vietnameseCommunication?: string;
    experienceIndustry?: string;
    experienceRequirement?: string;
    yearsOfExperience?: [number, number];
    yearsOfExperienceMin?: number;
    yearsOfExperienceMax?: number;
    specialConditions?: string[];
    hobbies?: string[];
    allergies?: string[];
    video?: string;
    videos?: string[];
    hasPhoto?: string;
    photos?: string[];
    docs?: string;
    docsSub?: string[];
    vision?: string;
    tattoo?: string;
    habit?: string;
    media?: string[];
    keyword?: string;
}

export const initialSearchFilters: SearchFilters = {
    q: '',
    visa: '',
    visaDetail: '',
    industry: '',
    jobDetail: '',
    location: [],
    gender: '',
    height: [140, 200],
    weight: [35, 100],
    age: [18, 45],
    visionRequirement: '',
    tattooRequirement: '',
    smokingStatus: '',
    drinkingStatus: '',
    dominantHand: '',
    bangladeshAgency: '',
    maritalStatus: '',
    childrenStatus: '',
    hasWorkedAbroad: '',
    workedCountries: [],
    currentSalary: '',
    currentSalaryMin: undefined,
    currentSalaryMax: undefined,
    desiredSalaryVietnam: '',
    desiredSalaryVietnamMin: undefined,
    desiredSalaryVietnamMax: undefined,
    educationRequirement: '',
    englishScore: '',
    englishLevel: '',
    vietnameseCertificate: '',
    vietnameseCommunication: '',
    experienceIndustry: '',
    experienceRequirement: '',
    yearsOfExperience: [0, 20],
    yearsOfExperienceMin: undefined,
    yearsOfExperienceMax: undefined,
    specialConditions: [],
    hobbies: [],
    allergies: [],
    video: '',
    videos: [],
    hasPhoto: '',
    photos: [],
    docs: '',
    docsSub: [],
    vision: '',
    tattoo: '',
    habit: '',
    media: [],
    keyword: '',
};

export const keyMap: { [key: string]: string } = {
    q: 'q',
    keyword: 'keyword',
    visa: 'loai-visa',
    visaDetail: 'chi-tiet-loai-hinh-visa',
    industry: 'nganh-nghe',
    jobDetail: 'chi-tiet-cong-viec',
    location: 'dia-diem',
    gender: 'gioi-tinh',
    age: 'do-tuoi',
    height: 'chieu-cao',
    weight: 'can-nang',
    visionRequirement: 'thi-luc',
    tattooRequirement: 'hinh-xam',
    dominantHand: 'tay-thuan',
    bangladeshAgency: 'cong-ty-phai-cu',
    maritalStatus: 'hon-nhan',
    childrenStatus: 'con-cai',
    hasWorkedAbroad: 'kinh-nghiem-nuoc-ngoai',
    workedCountries: 'cac-nuoc-da-lam',
    currentSalary: 'luong-hien-tai',
    currentSalaryMin: 'sal_c_min',
    currentSalaryMax: 'sal_c_max',
    desiredSalaryVietnam: 'sal_d_max',
    desiredSalaryVietnamMin: 'sal_d_min',
    desiredSalaryVietnamMax: 'sal_d_max_num',
    educationRequirement: 'hoc-van',
    englishRequirement: 'tieng-anh',
    vietnameseCertificate: 'tieng-viet-chung-chi',
    vietnameseCommunication: 'tieng-viet-giao-tiep',
    experienceIndustry: 'nganh-nghe-kinh-nghiem',
    experienceRequirement: 'linh-vuc-tay-nghe',
    yearsOfExperience: 'exp-years',
    yearsOfExperienceMin: 'exp_min',
    yearsOfExperienceMax: 'exp_max',
    specialConditions: 'dieu-kien-dac-biet',
    hobbies: 'so-thich',
    allergies: 'di-ung',
    video: 'video',
    videos: 'videos',
    hasPhoto: 'anh',
    photos: 'photos',
    docs: 'docs',
    docsSub: 'docs-sub',
};

export const reverseKeyMap: { [key: string]: string } = Object.fromEntries(
    Object.entries(keyMap).map(([key, value]) => [value, key])
);

/**
 * Chuyển đổi URL searchParams thành object filters để sử dụng cho Elasticsearch
 */
export function parseSearchParams(params: Record<string, string | string[] | undefined>): SearchFilters {
    const filters = { ...initialSearchFilters };
    
    Object.entries(params).forEach(([key, value]) => {
        const internalKey = reverseKeyMap[key] || key;
        
        if (!value) return;

        if (['age', 'height', 'weight', 'yearsOfExperience'].includes(internalKey)) {
            // Handle range [min, max]
            if (Array.isArray(value)) {
                // @ts-ignore
                filters[internalKey] = value.map(Number);
            } else if (typeof value === 'string') {
                if (value.includes(',')) {
                    // @ts-ignore
                    filters[internalKey] = value.split(',').map(Number);
                } else {
                    // Single number? (should be range)
                    // @ts-ignore
                    filters[internalKey] = [Number(value), Number(value)];
                }
            }
        } else if (['location', 'workedCountries', 'specialConditions', 'hobbies', 'allergies', 'videos', 'photos', 'docsSub'].includes(internalKey)) {
            // Handle arrays
            if (Array.isArray(value)) {
                // @ts-ignore
                filters[internalKey] = value;
            } else {
                // @ts-ignore
                filters[internalKey] = value.split(',').filter(Boolean);
            }
        } else if (['yearsOfExperienceMin', 'yearsOfExperienceMax', 'currentSalaryMin', 'currentSalaryMax', 'desiredSalaryVietnamMin', 'desiredSalaryVietnamMax'].includes(internalKey)) {
            // Handle numbers
            // @ts-ignore
            filters[internalKey] = Number(Array.isArray(value) ? value[0] : value);
        } else if (internalKey in initialSearchFilters) {
            // Handle strings
            // @ts-ignore
            if (Array.isArray(value)) filters[internalKey] = value[0];
            // @ts-ignore
            else filters[internalKey] = value;
        }
    });

    return filters;
}

import { industriesByJobType } from '@/doi-tac-module/lib/industry-data';

export function getIndustryKeyByLaborType(laborType?: string): keyof typeof industriesByJobType | undefined {
    switch (laborType) {
        case 'Lao động phổ thông': return 'thuc-tap-sinh-ky-nang';
        case 'Lao động kỹ thuật': return 'ky-nang-dac-dinh';
        case 'Kỹ sư, trí thức':
        case 'Kỹ sư, chuyên gia':
        case 'Kỹ sư, tri thức':
        case 'Kỹ sư, trí thức': return 'ky-su-tri-thuc';
        default: return undefined;
    }
}
